import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import type { CurrentPage } from 'src/shared/hooks/useSegmentHistory';
import { Provider as SegmentHistoryProvider } from 'src/shared/hooks/useSegmentHistory';
import type { RouteDescriptor } from 'src/shared/route';
import type { PageViewed } from 'src/shared/telemetry';
import { getRoute } from 'src/shared/utils/url';

const hasRequiredMetadata = (route: RouteDescriptor, metadata?: PageViewed) => {
  if (!route.metadata) {
    return true;
  }

  if (!metadata) {
    return false;
  }

  const propertyKeys = Object.keys(metadata);
  return route.metadata.every(key => propertyKeys.includes(key));
};

export const trackPage = ({
  route,
  metadata,
}: {
  route?: RouteDescriptor;
  metadata?: PageViewed;
}) => {
  if (
    !route?.page ||
    !hasRequiredMetadata(route, metadata) ||
    typeof analytics === 'undefined'
  ) {
    return;
  }
  analytics.page(route.page, metadata);
};

const Provider = ({ children }: PropsWithChildren<{}>) => {
  const { pathname } = useLocation();
  const currentRoute = getRoute(pathname);
  const [metadata, setMetadata] = useState<PageViewed>({});
  const context = useMemo(
    () => ({
      page: (currentRoute && 'page' in currentRoute
        ? currentRoute?.page
        : null) as CurrentPage,
      metadata,
      setMetadata,
    }),
    [metadata, setMetadata, currentRoute],
  );

  // Set and track the page on initial render
  // and whenever the path changes. This will
  // also track when the metadata changes because
  // sometimes the metadata is not available on
  // initial render and is set through `mergeMetadata`
  // later.
  useEffect(() => {
    trackPage({ route: currentRoute, metadata });
  }, [currentRoute, metadata]);

  return (
    <SegmentHistoryProvider value={context}>{children}</SegmentHistoryProvider>
  );
};

Provider.displayName = 'SegmentHistoryProvider';

export { Provider };
